import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { loginUser as loginUserApi, registerUser as registerUserApi, fetchFavoriteEstates } from '../api/api';
const initialState = {
    user: null,
    token: localStorage.getItem('token'),
    loading: false,
    error: null,
    favorites: [],
    isAuthenticated: !!localStorage.getItem('token'),
    currency: localStorage.getItem('currency') || 'EUR',
    language: localStorage.getItem('language') || 'en'
};

// Helper function to handle token storage consistently
const setTokenInStorage = (token) => {
    if (token) {
        localStorage.setItem('token', token);
    } else {
        localStorage.removeItem('token');
    }
};

export const loginUser = createAsyncThunk(
    'auth/login',
    async (credentials, { rejectWithValue, dispatch }) => {
        try {
            const { data } = await loginUserApi(credentials);
            setTokenInStorage(data.access_token);
            return {
                token: data.access_token,
                user: data.user
            };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const registerUser = createAsyncThunk(
    'auth/register',
    async (userData, { rejectWithValue }) => {
        try {
            const { data } = await registerUserApi(userData);
            setTokenInStorage(data.access_token);
            return {
                token: data.access_token,
                user: data.user
            };
        } catch (error) {
            // Check if error response contains a JSON structure with validation errors
            if (error.response && error.response.data) {
                return rejectWithValue(JSON.stringify(error.response.data));
            }
            return rejectWithValue(error.message);
        }
    }
);

export const getFavorites = createAsyncThunk(
    'auth/getFavorites',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await fetchFavoriteEstates();
            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setCurrency: (state, action) => {
            state.currency = action.payload;
            localStorage.setItem('currency', action.payload);
        },
        setLanguage: (state, action) => {
            state.language = action.payload;
            localStorage.setItem('language', action.payload);
        },
        logout: (state) => {
            setTokenInStorage(null);
            state.user = null;
            state.token = null;
            state.isAuthenticated = false;
            state.favorites = [];
        },
        setUser: (state, action) => {
            state.user = action.payload;
            state.isAuthenticated = !!state.token;
        },
        clearError: (state) => {
            state.error = null;
        },
        addFavorite: (state, action) => {
            state.favorites.push(action.payload);
        },
        removeFavorite: (state, action) => {
            state.favorites = state.favorites.filter(fav => fav.estate_id !== action.payload);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(loginUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase('auth/SET_FAVORITES', (state, action) => {
                state.favorites = action.payload;
            })
            .addCase('auth/SET_TOKEN_AND_USER', (state, action) => {
                state.loading = false;
                state.user = action.payload.user;
                state.token = action.payload.token;
                state.isAuthenticated = true;
                state.error = null;
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload.user;
                state.token = action.payload.token;
                state.isAuthenticated = true;
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(registerUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(registerUser.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload.user;
                state.token = action.payload.token;
                state.isAuthenticated = true;
            })
            .addCase(registerUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(getFavorites.fulfilled, (state, action) => {
                state.favorites = action.payload;
            });
    }
});

export const { logout, setUser, clearError, addFavorite, removeFavorite, setCurrency, setLanguage } = authSlice.actions;
export default authSlice.reducer;